import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import type {
  AgencyEditorPatchAttemptSummaryV1,
  StudioDepartureKitStatusV1,
  StudioIntakeSummaryV1,
  StudioProjectLifecycleViewV1,
  StudioSafeEditPresentationV1,
} from "@enrein/shared-contracts";
import type { BusinessGraphActiveViewV1 } from "@enrein/shared-contracts/studio-project-workflow";
import type { StudioRunRowV1 } from "@enrein/shared-contracts/studio-dashboard";
import type { StudioApiClient } from "../../../api.js";
import { loadIntakeSummary } from "../workflow/studio-workflow.js";
import { deriveStudioProjectLifecycle } from "./project-phase.js";

export interface StudioProjectSnapshot {
  readonly lifecycle: StudioProjectLifecycleViewV1;
  readonly review: ClientReviewStatusV1;
  readonly status: Awaited<ReturnType<StudioApiClient["getBusinessGraphStatus"]>>;
  readonly runRows: StudioRunRowV1[];
  readonly businessName: string | null;
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly activeGraph: BusinessGraphActiveViewV1 | null;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  /** Existing agency-editor patch attempts for Slice D presentation (ENR-UX-009). */
  readonly agencyEditorPatchAttempts: readonly AgencyEditorPatchAttemptSummaryV1[];
  /** Server-resolved Agency projection of the client safe-edit attempt (ENR-UX-009 Slice D). */
  readonly safeEditPresentation: StudioSafeEditPresentationV1 | null;
}

function isLifecycleEndpointMissing(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const message = error.message.toLowerCase();
  return (
    message === "not found" ||
    message.includes("studio_api_error_404") ||
    message.includes("/lifecycle not found") ||
    message.includes("project-lifecycle")
  );
}

async function loadProjectLifecycleFallback(
  api: StudioApiClient,
  projectId: string,
): Promise<StudioProjectLifecycleViewV1> {
  const [review, status, runRows, intakeSummary] = await Promise.all([
    api.getClientReviewStatus(projectId),
    api.getBusinessGraphStatus(projectId),
    api.listRuns(projectId),
    loadIntakeSummary(api, projectId).catch(() => null),
  ]);
  const intakeSubmitted =
    intakeSummary?.status === "SUBMITTED" || review.clientLink?.status === "INTAKE_SUBMITTED";
  let departureKit: StudioDepartureKitStatusV1 | null = null;
  if (review.latestApproval) {
    try {
      departureKit = await api.getDepartureKitStatus(projectId);
    } catch {
      departureKit = null;
    }
  }
  return deriveStudioProjectLifecycle({
    agencyId: status.agencyId,
    projectId,
    generatedAt: new Date().toISOString(),
    runs: runRows,
    clientReview: review,
    departureKit,
    intakeSummary,
    hasActiveRevision: Boolean(status.currentBusinessGraphRevisionId),
    intakeSubmitted,
    generationLock: { locked: false, runId: null, lockedAt: null },
  });
}

async function loadProjectLifecycle(
  api: StudioApiClient,
  projectId: string,
): Promise<StudioProjectLifecycleViewV1> {
  try {
    return await api.getProjectLifecycle(projectId);
  } catch (error) {
    if (!isLifecycleEndpointMissing(error)) {
      throw error;
    }
    return loadProjectLifecycleFallback(api, projectId);
  }
}

export async function refreshProjectSnapshot(
  api: StudioApiClient,
  projectId: string,
  options?: { readonly includeDepartureKit?: boolean },
): Promise<StudioProjectSnapshot> {
  const [lifecycle, status, runRows, active, agencyEditorPatchAttempts, safeEditPresentation] =
    await Promise.all([
      loadProjectLifecycle(api, projectId),
      api.getBusinessGraphStatus(projectId),
      api.listRuns(projectId),
      api.getActiveBusinessGraph(projectId).catch(() => null),
      api.listAgencyEditorPatchAttempts(projectId).catch(() => [] as AgencyEditorPatchAttemptSummaryV1[]),
      // Fail closed: if the exact safe-edit projection is missing or corrupt, do not render a
      // project snapshot whose latest unrelated run could masquerade as the safe-edit status.
      api.getSafeEditPresentation(projectId),
    ]);
  const review = lifecycle.clientReview ?? (await api.getClientReviewStatus(projectId));
  const intakeSummary =
    lifecycle.intakeSummary ?? (await loadIntakeSummary(api, projectId).catch(() => null));
  let departureKit = lifecycle.departureKit ?? null;
  if (options?.includeDepartureKit === false) {
    departureKit = null;
  } else if (!departureKit && lifecycle.flags.hasApproval) {
    try {
      departureKit = await api.getDepartureKitStatus(projectId);
    } catch {
      departureKit = null;
    }
  }
  return {
    lifecycle,
    review,
    status,
    runRows,
    businessName:
      active?.businessGraph.business.businessName ??
      intakeSummary?.businessName ??
      lifecycle.clientReview?.clientLink?.clientDisplayName ??
      null,
    intakeSummary,
    activeGraph: active,
    departureKit,
    agencyEditorPatchAttempts,
    safeEditPresentation,
  };
}
